"use client";

import type { JobItemDto } from "@artha/api-contracts";
import { unescapeHtmlMarkup } from "@/lib/utils/htmlEntities";

export const sanitizeDescription = (html?: string) => {
  if (!html) return "";

  // Some feeds deliver the description entity-escaped ("&lt;h1&gt;...") — unwrap it
  // so the markup is parsed instead of rendered as literal tag text.
  const markup = unescapeHtmlMarkup(html);

  // Use DOM parsing so we can remove unwanted tags and attributes while keeping allowed text/structure.
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return markup.replace(/<[^>]*>/g, "");
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(markup, "text/html");
  const allowedTags = new Set(["p", "br", "strong", "em", "b", "i", "u", "ul", "ol", "li", "a"]);
  const headingTags = new Set(["h1", "h2", "h3", "h4", "h5", "h6"]);

  const sanitizeNode = (node: Node): Node | null => {
    if (node.nodeType === Node.TEXT_NODE) return node.cloneNode(true);
    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();

    if (tag === "script" || tag === "style") return null;

    if (headingTags.has(tag)) {
      const p = doc.createElement("p");
      element.childNodes.forEach((child) => {
        const sanitizedChild = sanitizeNode(child);
        if (sanitizedChild) p.appendChild(sanitizedChild);
      });
      return p;
    }

    if (!allowedTags.has(tag)) {
      const fragment = doc.createDocumentFragment();
      element.childNodes.forEach((child) => {
        const sanitizedChild = sanitizeNode(child);
        if (sanitizedChild) fragment.appendChild(sanitizedChild);
      });
      return fragment;
    }

    const cleanEl = doc.createElement(tag);
    if (tag === "ul") {
      cleanEl.style.listStyleType = "disc";
      cleanEl.style.paddingLeft = "1.25em";
      cleanEl.style.margin = "0.5em 0";
    }

    if (tag === "ol") {
      cleanEl.style.listStyleType = "decimal";
      cleanEl.style.paddingLeft = "1.25em";
      cleanEl.style.margin = "0.5em 0";
    }

    if (tag === "a") {
      const href = element.getAttribute("href") || "";
      const isJavascriptLink = href.trim().toLowerCase().startsWith("javascript:");
      if (!isJavascriptLink && href) {
        cleanEl.setAttribute("href", href);
      }
      if (element.getAttribute("target") === "_blank") {
        cleanEl.setAttribute("target", "_blank");
        cleanEl.setAttribute("rel", "noopener noreferrer");
      }
    }

    element.childNodes.forEach((child) => {
      const sanitizedChild = sanitizeNode(child);
      if (sanitizedChild) cleanEl.appendChild(sanitizedChild);
    });

    return cleanEl;
  };

  const fragment = doc.createDocumentFragment();
  doc.body.childNodes.forEach((child) => {
    const sanitizedChild = sanitizeNode(child);
    if (sanitizedChild) fragment.appendChild(sanitizedChild);
  });

  const wrapper = doc.createElement("div");
  wrapper.appendChild(fragment);

  // Drop empty paragraphs (including ones with only whitespace or non-breaking spaces).
  wrapper.querySelectorAll("p").forEach((p) => {
    const text = (p.textContent || "").replace(/\u00a0/g, " ").trim();
    if (!text) {
      p.remove();
    }
  });

  return wrapper.innerHTML.trim();
};

export const formatExperience = (job: JobItemDto) => {
  const min = job.exp_min;
  const max = job.exp_max;
  const unit = job.exp_unit ? ` ${job.exp_unit}` : "";

  if (min != null && max != null) return `${min} - ${max}${unit}`;
  if (min != null) return `${min}${unit}`;
  if (max != null) return `${max}${unit}`;
  return "";
};

export { isRphJob } from "@artha/utils";

